import type { BlockHeightScale, PrecisionMinutes } from '@/core/types/settings'
import type { RowHeights } from './timelineWindow'

/**
 * Timeline sizing. All values in CSS pixels, from 03-ui-spec.md section 2.1.
 *
 * Blocks are deliberately shorter than the 44px touch-target guideline: a 44px
 * block would put only eleven hours on an iPhone screen, which defeats the
 * product. The mitigations are documented in the spec -- full-width targets,
 * drag selection, and coarser tapping at fine precisions.
 */

const BASE_ROW_HEIGHT: Record<PrecisionMinutes, number> = {
  60: 40,
  30: 26,
  15: 18,
  10: 15,
  5: 12,
}

const SCALE: Record<BlockHeightScale, number> = {
  compact: 0.8,
  normal: 1.0,
  large: 1.3,
}

export const GUTTER_WIDTH = 44
export const DIVIDER_HEIGHT = 26

export function rowHeightFor(
  precision: PrecisionMinutes,
  scale: BlockHeightScale,
): number {
  return Math.round(BASE_ROW_HEIGHT[precision] * SCALE[scale])
}

export function heightsFor(
  precision: PrecisionMinutes,
  scale: BlockHeightScale,
): RowHeights {
  return { row: rowHeightFor(precision, scale), divider: DIVIDER_HEIGHT }
}

/**
 * Below this row height a block cannot legibly hold text, so labels are
 * dropped and colour plus the status toolbar carry the information.
 *
 * The CSS caps label size at roughly 0.46 x the row height so text can never
 * overflow its block. That protects the layout but not legibility: at an 18px
 * row the label would shrink to about 8px, which is unreadable and below the
 * design system's floor. 20px keeps the smallest rendered label around 9px.
 */
export const MIN_HEIGHT_FOR_LABEL = 20
